import Fuse from 'fuse.js';
import { createElement } from './util';
import { getCues } from './cache-cues';
import is from './is';

/**
 *
 * @param {TextTrack} track
 * @returns Cues fron the track
 */
export const vttToJson = track => {
  if (!track) return null;

  const ret = ['kind', 'label', 'language', 'id', 'inBandMetadataTrackDispatchType', 'mode', 'src'].reduce(
    (acc, prop) => {
      if (track[prop]) {
        acc[prop] = track[prop];
      }
      return acc;
    },
    {
      cues:
        track.cues &&
        Array.prototype.map.call(track.cues, function (cue) {
          return {
            startTime: cue.startTime,
            endTime: cue.endTime,
            text: cue.text,
            id: cue.id,
          };
        }),
    },
  );

  return ret;
};

/**
 *
 * @param {String} searchKeyword
 * @param {JSON} list
 * @returns Object of search results
 */
const fuzzySearch = (searchKeyword, list, preset) => {
  if (is.empty(list)) return [];
  if (is.empty(preset)) return [];
  if (!is.string(searchKeyword)) return [];

  /**
   * Default settings for the Fusejs library.
   * Reference - https://fusejs.io/api/options.html
   *
   * isCaseSensitive: false
   * includeScore: true
   * shouldSort: true
   * includeMatches: true
   * findAllMatches: false
   * minMatchCharLength: 1
   * location: 0
   * threshold: 0.3
   * distance: 100
   * useExtendedSearch: false
   * ignoreLocation: false
   * ignoreFieldNorm: false
   * fieldNormWeight: 1
   *
   */
  const options = {
    ...preset?.minMatchCharLength,
    ...preset?.threshold,
    shouldSort: false,
    includeMatches: true,
    threshold: 0.3,
    keys: ['text'],
  };

  if (null === list || !list || 0 === list.length) {
    return [];
  }

  const fuseObject = new Fuse(list, options);
  return fuseObject.search(searchKeyword);
};

/**
 *
 * @param {String} searchString
 * @param {JSON} list
 * @returns Array of time in seconds where the search string is found.
 */
const searchInJson = (searchString, list, preset) => {
  if (!is.string(searchString)) return [];
  if (is.empty(list)) return [];
  if (is.empty(preset)) return [];

  const searchResult = fuzzySearch(searchString, list, preset);

  return (
    searchResult &&
    Array.prototype.map.call(searchResult, function (result) {
      return {
        time: Math.ceil(result.item.startTime),
        label: ''
      };
    })
  );
};

/**
 *
 * @param {any} player
 * @param {string} searchString
 * @returns Array of time in seconds where the search string is found.
 */
export function searchVtt(player, searchString) {
  if (!player) return;
  if (!player?.media?.textTracks || player?.media?.textTracks?.length === 0) return;

  const track = player.media.textTracks;
  const preset = player.config.preset?.search || '';

  let cues = '';
  if (player.captions.currentTrack === -1) {
    // When no track is selected, grab the cues from cache localstorage.
    cues = getCues(player);
  } else {
    cues = track[player.captions.currentTrack];
  }

  if (is.empty(cues)) return [];

  const jsonTrack = vttToJson(cues);

  if (is.empty(jsonTrack)) return [];

  return searchInJson(searchString, jsonTrack?.cues, preset);
}

export function removeOldMarkers(player) {
  if (!player) return;
  if (!player?.elements || !player?.elements?.progress) return;

  // Remove old markers.
  let oldMarkers = player.elements.progress.querySelectorAll('.plyr__progress__marker');
  if (is.empty(oldMarkers)) return;
  for (var i = 0; i < oldMarkers.length; i++) {
    oldMarkers[i].remove();
  }
}

export function setMarkers(player, points) {
  if (!player) return;

  removeOldMarkers(player);

  if (!points?.length) return;

  const containerFragment = document.createDocumentFragment();
  const pointsFragment = document.createDocumentFragment();

  // Inject markers to progress container
  points.forEach(point => {
    const markerElement = createElement(
      'span',
      {
        class: 'plyr__progress__marker',
      },
      '',
    );

    const left = `${(point.time / player.duration) * 100}%`;

    markerElement.addEventListener('click', () => {
      player.currentTime = point.time;
    });

    markerElement.style.left = left;
    pointsFragment.appendChild(markerElement);
  });

  containerFragment.appendChild(pointsFragment);

  player.elements.markers = {
    points: pointsFragment,
    tip: null,
  };

  player.elements.progress.appendChild(containerFragment);
}
